export type SqliteFunctionDoc = {
  name: string;
  signature?: string;
  description: string;
};

export const SQLITE_KEYWORDS: readonly string[] = [
  'SELECT',
  'FROM',
  'WHERE',
  'JOIN',
  'INNER',
  'LEFT',
  'RIGHT',
  'FULL',
  'CROSS',
  'ON',
  'GROUP',
  'BY',
  'HAVING',
  'ORDER',
  'ASC',
  'DESC',
  'LIMIT',
  'OFFSET',
  'INSERT',
  'INTO',
  'VALUES',
  'UPDATE',
  'SET',
  'DELETE',
  'CREATE',
  'TABLE',
  'VIEW',
  'INDEX',
  'TRIGGER',
  'DROP',
  'ALTER',
  'WITH',
  'AS',
  'DISTINCT',
  'UNION',
  'ALL',
  'EXISTS',
  'IN',
  'LIKE',
  'BETWEEN',
  'IS',
  'NULL',
  'NOT',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'PRAGMA',
  'EXPLAIN',
  'VACUUM',
  'ANALYZE',
] as const;

const KEYWORD_DOCS: Record<string, string> = {
  SELECT: 'Return rows from a query.',
  FROM: 'Specify the source table(s) or subquery.',
  WHERE: 'Filter rows by a condition.',
  JOIN: 'Combine rows from multiple tables.',
  GROUP: 'Group rows for aggregate functions.',
  ORDER: 'Sort result rows.',
  LIMIT: 'Limit the number of returned rows.',
  INSERT: 'Insert new rows into a table.',
  UPDATE: 'Update existing rows in a table.',
  DELETE: 'Delete rows from a table.',
  CREATE: 'Create a database object (table, view, index, …).',
  TABLE: 'A table object.',
  VIEW: 'A stored query (view).',
  PRAGMA: 'Query or set SQLite runtime parameters.',
  EXPLAIN: 'Show query execution details.',
};

export const SQLITE_FUNCTIONS: readonly SqliteFunctionDoc[] = [
  { name: 'COUNT', signature: 'COUNT(expr)', description: 'Count rows or non-NULL values.' },
  { name: 'SUM', signature: 'SUM(expr)', description: 'Sum numeric values.' },
  { name: 'AVG', signature: 'AVG(expr)', description: 'Average numeric values.' },
  { name: 'MIN', signature: 'MIN(expr)', description: 'Minimum value.' },
  { name: 'MAX', signature: 'MAX(expr)', description: 'Maximum value.' },
  { name: 'LOWER', signature: 'LOWER(text)', description: 'Convert text to lowercase.' },
  { name: 'UPPER', signature: 'UPPER(text)', description: 'Convert text to uppercase.' },
  { name: 'LENGTH', signature: 'LENGTH(text)', description: 'Length of text (in characters).' },
  { name: 'SUBSTR', signature: 'SUBSTR(text, start, length)', description: 'Substring of text.' },
  { name: 'COALESCE', signature: 'COALESCE(a, b, ...)', description: 'First non-NULL argument.' },
  { name: 'IFNULL', signature: 'IFNULL(a, b)', description: 'b if a is NULL; otherwise a.' },
  { name: 'NULLIF', signature: 'NULLIF(a, b)', description: 'NULL if a equals b; otherwise a.' },
  { name: 'DATE', signature: 'DATE(timestring)', description: 'Convert to date.' },
  { name: 'TIME', signature: 'TIME(timestring)', description: 'Convert to time.' },
  { name: 'DATETIME', signature: 'DATETIME(timestring)', description: 'Convert to datetime.' },
  { name: 'JULIANDAY', signature: 'JULIANDAY(timestring)', description: 'Julian day number.' },
] as const;

export function getSqliteKeywordDoc(keyword: string): string | undefined {
  return KEYWORD_DOCS[keyword.toUpperCase()];
}

export function getSqliteFunctionDoc(name: string): SqliteFunctionDoc | undefined {
  const upper = name.toUpperCase();
  return SQLITE_FUNCTIONS.find((fn) => fn.name === upper);
}

